import { PremiumGlassCard } from '@/components/ui'
import { Link, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuthStore } from '../stores/authStore'
import { useLocale } from '../hooks/useLocale'
import { Fingerprint, Shield, Lock, Chrome, Github, Key, Loader2, Smartphone, CreditCard } from 'lucide-react'
import EncryptionField from '../components/EncryptionField'
import WalletSignInPanel from '../components/auth/WalletSignInPanel'
import DeviceApprovalQR from '../components/auth/DeviceApprovalQR'

/**
 * LoginPage — "The Vault Door"
 *
 * Split-screen layout:
 *   Left: EncryptionField visualization + identity statement + trust badges
 *   Right: Auth card with hierarchy:
 *     1. Sign in with Apple  (white button, Apple guidelines)
 *     2. Passkey / Face ID   (teal gradient, platform-aware label)
 *     3. Google / GitHub     (compact 2-column)
 *     4. Email + password    (collapsed fallback)
 */

const brandFontStyle: React.CSSProperties = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", "Helvetica", Arial, sans-serif',
  fontWeight: 100,
}

const staggerItem = (i: number) => ({
  initial: { opacity: 0, y: 15 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay: 0.15 + i * 0.06, ease: [0.16, 1, 0.3, 1] as const },
})

function detectBiometricLabel(): 'Face ID' | 'Touch ID' | 'Passkey' {
  const ua = navigator.userAgent
  const platform = (navigator as any).platform || ''
  if (/iPhone|iPad|iPod/.test(ua)) return 'Face ID'
  if (/Mac/.test(platform) || /Macintosh/.test(ua)) return 'Touch ID'
  return 'Passkey'
}

type LoadingMethod = 'passkey' | 'apple' | 'google' | 'github' | 'email' | null
type AltMethod = 'wallet' | 'device' | null

export default function LoginPage() {
  const navigate = useNavigate()
  const { t, tNested } = useLocale()
  const {
    login, loginWithPasskey, loginWithApple, loginWithOAuth, loginWithToken,
    isLoading, error, clearError, isAuthenticated,
  } = useAuthStore()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [loadingMethod, setLoadingMethod] = useState<LoadingMethod>(null)
  const [hasPlatformAuth, setHasPlatformAuth] = useState(false)
  const [biometricLabel, setBiometricLabel] = useState<'Face ID' | 'Touch ID' | 'Passkey'>('Passkey')
  const [altMethod, setAltMethod] = useState<AltMethod>(null)

  // Detect platform authenticator (Face ID / Touch ID)
  useEffect(() => {
    if (
      typeof PublicKeyCredential !== 'undefined' &&
      typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function'
    ) {
      PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then((available) => {
          setHasPlatformAuth(available)
          if (available) setBiometricLabel(detectBiometricLabel())
        })
        .catch(() => setHasPlatformAuth(false))
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard')
  }, [isAuthenticated, navigate])

  useEffect(() => {
    clearError()
  }, [clearError])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoadingMethod('email')
    try {
      await login({ email, password, rememberMe: true })
    } catch (err) {
      console.error('Login failed:', err)
    } finally {
      setLoadingMethod(null)
    }
  }

  const handlePasskeyLogin = async () => {
    setLoadingMethod('passkey')
    try {
      await loginWithPasskey()
    } catch (err) {
      console.error('Passkey login failed:', err)
    } finally {
      setLoadingMethod(null)
    }
  }

  const handleAppleLogin = async () => {
    setLoadingMethod('apple')
    try {
      await loginWithApple()
    } catch (err) {
      const msg = err instanceof Error ? err.message : ''
      if (msg !== 'popup_closed_by_user') {
        console.error('Apple login failed:', err)
      }
    } finally {
      setLoadingMethod(null)
    }
  }

  const handleOAuthLogin = (provider: 'google' | 'github') => {
    loginWithOAuth(provider)
  }

  const biometricButtonLabel = (() => {
    if (!hasPlatformAuth) return tNested('login', 'passkey', 'signInWithPasskey')
    if (biometricLabel === 'Face ID') return tNested('login', 'passkey', 'continueWithFaceId')
    if (biometricLabel === 'Touch ID') return tNested('login', 'passkey', 'continueWithTouchId')
    return tNested('login', 'passkey', 'continueWithBiometrics')
  })()

  const anyLoading = isLoading || loadingMethod !== null

  return (
    <div className="min-h-[85vh] flex items-center justify-center px-6 py-16 relative overflow-hidden">
      <div className="relative z-10 w-full max-w-5xl">
        <div className="flex flex-col lg:flex-row gap-0 lg:gap-16 items-center">

          {/* ─── Left Panel ─── */}
          <motion.div
            className="hidden lg:flex flex-col items-center justify-center flex-1 max-w-md"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="relative w-[320px] h-[320px] mb-10">
              <EncryptionField />
            </div>

            <div className="text-center space-y-1 mb-10">
              <p className="text-4xl font-thin text-white/90 tracking-tight" style={brandFontStyle}>
                Your identity.
              </p>
              <p className="text-4xl font-thin text-teal-400/80 tracking-tight" style={brandFontStyle}>
                Sovereign.
              </p>
              <p className="text-4xl font-thin text-white/40 tracking-tight" style={brandFontStyle}>
                Encrypted.
              </p>
            </div>

            <div className="space-y-3">
              {[
                { icon: Shield, label: 'End-to-end encrypted' },
                { icon: Fingerprint, label: 'WebAuthn passkey support' },
                { icon: Lock, label: 'Zero-knowledge architecture' },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.8 + i * 0.1 }}
                >
                  <item.icon className="w-3.5 h-3.5 text-teal-400/50" />
                  <span className="text-[11px] text-white/40 uppercase tracking-[0.15em]">{item.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* ─── Right Panel: Auth Card ─── */}
          <motion.div
            className="w-full lg:flex-1 max-w-md"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="lg:hidden text-center mb-8">
              <p className="text-2xl font-thin text-white/70 tracking-tight" style={brandFontStyle}>
                Your identity. <span className="text-teal-400/80">Sovereign.</span>
              </p>
            </div>

            <PremiumGlassCard
              accent="dream"
              dark
              hover={false}
              size="default"
              style={{
                backdropFilter: 'blur(40px) saturate(200%)',
                WebkitBackdropFilter: 'blur(40px) saturate(200%)',
                background: 'rgba(255, 255, 255, 0.04)',
              }}
            >
              <div className="p-8 md:p-10">

                {/* Header */}
                <motion.div className="text-center mb-8" {...staggerItem(0)}>
                  <Link to="/" className="inline-block mb-4">
                    <span className="text-3xl tracking-[0.15em] text-white" style={brandFontStyle}>
                      ΛiD
                    </span>
                  </Link>
                  <p className="text-white/50 text-sm font-light tracking-wide">
                    {t('login', 'subtitle')}
                  </p>
                </motion.div>

                {/* Error */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg"
                  >
                    <p className="text-red-400 text-sm flex items-center gap-2">
                      <Shield className="w-4 h-4 flex-shrink-0" />
                      {error}
                    </p>
                  </motion.div>
                )}

                {/* ── 1. Sign in with Apple ── */}
                <motion.button
                  type="button"
                  onClick={handleAppleLogin}
                  disabled={anyLoading}
                  className="w-full py-[14px] rounded-xl text-sm font-medium tracking-wide
                           flex items-center justify-center gap-3
                           bg-white text-black
                           hover:bg-white/90
                           disabled:opacity-40 disabled:cursor-not-allowed
                           transition-all duration-200"
                  whileHover={!anyLoading ? { scale: 1.01 } : {}}
                  whileTap={!anyLoading ? { scale: 0.99 } : {}}
                  {...staggerItem(1)}
                  aria-label="Sign in with Apple"
                >
                  {loadingMethod === 'apple' ? (
                    <Loader2 className="w-5 h-5 animate-spin text-black" />
                  ) : (
                    <svg
                      viewBox="0 0 814 1000"
                      className="w-5 h-5 text-black flex-shrink-0"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 383.2 0 281 0 185.4c0-113 73.1-172.7 145.1-172.7 38.1 0 69.8 26.3 93.3 26.3 22.3 0 57.7-27.7 97.4-27.7 16.2.1 71.9 1.9 115 56.3zm-254.4-59.1c5.1-25.7 17.9-51.4 35.2-71.5 22.3-25.7 51.4-43.9 78.4-43.9 1.3 11.6 2.6 23.1 2.6 36.3 0 23.8-3.9 48.3-15.5 69.3-10.4 19.4-33.5 45.1-69.3 45.1-2 0-3.9-.6-5.8-.6l-25.6-34.7z" />
                    </svg>
                  )}
                  <span>{tNested('login', 'oauth', 'signInWithApple')}</span>
                </motion.button>

                {/* Divider */}
                <motion.div className="flex items-center gap-4 my-5" {...staggerItem(2)}>
                  <div className="h-px flex-1 bg-white/10" />
                  <span className="text-[10px] text-white/30 uppercase tracking-[0.2em]">or</span>
                  <div className="h-px flex-1 bg-white/10" />
                </motion.div>

                {/* ── 2. Passkey / Face ID / Touch ID ── */}
                <motion.button
                  type="button"
                  onClick={handlePasskeyLogin}
                  disabled={anyLoading}
                  className="w-full py-[14px] rounded-xl font-light text-sm tracking-wide text-white
                           bg-gradient-to-r from-teal-600 to-cyan-600
                           hover:from-teal-500 hover:to-cyan-500
                           disabled:opacity-40 disabled:cursor-not-allowed
                           transition-all duration-300 flex items-center justify-center gap-3"
                  style={{
                    boxShadow: '0 0 20px rgba(20, 184, 166, 0.25), 0 4px 12px rgba(0, 0, 0, 0.3)',
                  }}
                  whileHover={!anyLoading ? { scale: 1.01 } : {}}
                  whileTap={!anyLoading ? { scale: 0.99 } : {}}
                  {...staggerItem(3)}
                >
                  {loadingMethod === 'passkey' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Fingerprint className="w-5 h-5" />
                  )}
                  {biometricButtonLabel}
                </motion.button>

                {/* Divider */}
                <motion.div className="flex items-center gap-4 my-5" {...staggerItem(4)}>
                  <div className="h-px flex-1 bg-white/10" />
                  <span className="text-[10px] text-white/30 uppercase tracking-[0.2em]">or</span>
                  <div className="h-px flex-1 bg-white/10" />
                </motion.div>

                {/* ── 3. Google / GitHub ── */}
                <motion.div className="grid grid-cols-2 gap-3" {...staggerItem(5)}>
                  <button
                    type="button"
                    onClick={() => handleOAuthLogin('google')}
                    disabled={anyLoading}
                    className="flex items-center justify-center gap-2 py-3 rounded-lg
                             bg-white/[0.03] border border-white/10
                             hover:border-white/20 hover:bg-white/[0.06]
                             text-white/60 hover:text-white/80 text-sm font-light
                             disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <Chrome className="w-4 h-4 flex-shrink-0" />
                    <span>{tNested('login', 'oauth', 'google')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOAuthLogin('github')}
                    disabled={anyLoading}
                    className="flex items-center justify-center gap-2 py-3 rounded-lg
                             bg-white/[0.03] border border-white/10
                             hover:border-white/20 hover:bg-white/[0.06]
                             text-white/60 hover:text-white/80 text-sm font-light
                             disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <Github className="w-4 h-4 flex-shrink-0" />
                    <span>{tNested('login', 'oauth', 'github')}</span>
                  </button>
                </motion.div>

                {/* ── 4. Email / password — collapsed ── */}
                <motion.div className="mt-3" {...staggerItem(6)}>
                  {!showEmailForm ? (
                    <button
                      type="button"
                      onClick={() => setShowEmailForm(true)}
                      className="w-full py-3 text-[11px] text-white/35 uppercase tracking-[0.15em]
                               hover:text-white/55 transition-colors duration-200"
                    >
                      Sign in with email
                    </button>
                  ) : (
                    <motion.form
                      onSubmit={handleSubmit}
                      className="space-y-4 pt-3"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.3 }}
                    >
                      <input
                        id="email"
                        data-testid="email-input"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={anyLoading}
                        className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-lg
                                 text-white placeholder-white/25 text-sm
                                 focus:outline-none focus:ring-1 focus:ring-teal-500/50 focus:border-teal-500/30
                                 transition-all disabled:opacity-50"
                        placeholder={tNested('login', 'form', 'emailPlaceholder')}
                        autoComplete="email"
                      />

                      <div className="relative">
                        <input
                          id="password"
                          data-testid="password-input"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          disabled={anyLoading}
                          className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-lg
                                   text-white placeholder-white/25 text-sm
                                   focus:outline-none focus:ring-1 focus:ring-teal-500/50 focus:border-teal-500/30
                                   transition-all disabled:opacity-50"
                          placeholder={tNested('login', 'form', 'passwordPlaceholder')}
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/50 text-xs transition-colors"
                        >
                          {showPassword ? 'Hide' : 'Show'}
                        </button>
                      </div>

                      <div className="flex justify-end">
                        <Link
                          to="/forgot-password"
                          className="text-xs text-teal-400/60 hover:text-teal-400/80 transition-colors"
                        >
                          {tNested('login', 'form', 'forgotPassword')}
                        </Link>
                      </div>

                      <button
                        type="submit"
                        disabled={anyLoading}
                        className="w-full py-3 rounded-lg text-sm font-light text-white
                                 bg-white/[0.06] border border-white/10
                                 hover:border-white/20 hover:bg-white/[0.1]
                                 disabled:opacity-50 disabled:cursor-not-allowed
                                 transition-all duration-200 flex items-center justify-center gap-2"
                      >
                        {loadingMethod === 'email' ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            {tNested('login', 'form', 'signingIn')}
                          </>
                        ) : (
                          tNested('login', 'form', 'submitButton')
                        )}
                      </button>
                    </motion.form>
                  )}
                </motion.div>

                {/* ── 5. Wallet Pass sign-in ── */}
                <motion.div className="mt-3" {...staggerItem(7)}>
                  <button
                    type="button"
                    onClick={() => setAltMethod(altMethod === 'wallet' ? null : 'wallet')}
                    className="w-full py-3 flex items-center justify-center gap-2
                             text-[11px] text-white/35 uppercase tracking-[0.15em]
                             hover:text-white/55 transition-colors duration-200"
                  >
                    <CreditCard className="w-3.5 h-3.5" />
                    Use LUKHΛS pass
                  </button>
                  <AnimatePresence>
                    {altMethod === 'wallet' && (
                      <motion.div
                        key="wallet-panel"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div className="pt-3">
                          <WalletSignInPanel
                            mode="login"
                            onSuccess={(token) => loginWithToken(token)}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* ── 6. Approve from another device ── */}
                <motion.div className="mt-1" {...staggerItem(8)}>
                  <button
                    type="button"
                    onClick={() => setAltMethod(altMethod === 'device' ? null : 'device')}
                    className="w-full py-3 flex items-center justify-center gap-2
                             text-[11px] text-white/35 uppercase tracking-[0.15em]
                             hover:text-white/55 transition-colors duration-200"
                  >
                    <Smartphone className="w-3.5 h-3.5" />
                    Approve from another device
                  </button>
                  <AnimatePresence>
                    {altMethod === 'device' && (
                      <motion.div
                        key="device-qr"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div className="pt-3">
                          <DeviceApprovalQR
                            onSuccess={(token) => loginWithToken(token)}
                            onCancel={() => setAltMethod(null)}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Register link */}
                <motion.div className="mt-6 pt-6 border-t border-white/5 text-center" {...staggerItem(9)}>
                  <p className="text-white/40 text-sm font-light">
                    {tNested('login', 'register', 'noAccount')}{' '}
                    <Link to="/register" className="text-teal-400 hover:text-teal-300 transition-colors">
                      {tNested('login', 'register', 'createNow')}
                    </Link>
                  </p>
                </motion.div>

                {/* SSO hint */}
                <motion.div className="mt-4 text-center" {...staggerItem(10)}>
                  <p className="text-[10px] text-white/25 flex items-center justify-center gap-1.5">
                    <Key className="w-3 h-3" />
                    {tNested('login', 'sso', 'info')}
                  </p>
                </motion.div>

              </div>
            </PremiumGlassCard>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
